import { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Cake, Calendar, MessageCircle, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const MESES = [
  'Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const MENSAGENS = [
  (nome) => `A paz de Deus, tudo bem? Feliz aniversário, ${nome}! Que este novo ano da sua vida seja cheio de saúde, paz e alegria! Deus abençoe.`,
  (nome) => `A paz de Deus, tudo bem? Parabéns, ${nome}! Que o Senhor continue guiando seus passos e enchendo sua vida de bênçãos! Deus abençoe.`,
  (nome) => `A paz de Deus, tudo bem? Feliz aniversário, ${nome}! Que este dia seja repleto de amor, alegria e a presença de Deus! Deus abençoe.`,
  (nome) => `A paz de Deus, tudo bem? Parabéns pelo seu dia, ${nome}! Que Deus renove suas forças e multiplique suas bênçãos neste novo ciclo! Deus abençoe.`,
  (nome) => `A paz de Deus, tudo bem? Feliz aniversário, ${nome}! Celebramos a vida que Deus te deu! Muita paz e prosperidade! Deus abençoe.`,
];

function enviarWhatsApp(nome, whatsapp) {
  const numero = whatsapp.replace(/\D/g, '');
  const msg = MENSAGENS[Math.floor(Math.random() * MENSAGENS.length)](nome);
  const url = `https://wa.me/55${numero}?text=${encodeURIComponent(msg)}`;
  // Try to open directly (works in production, blocked in preview iframe)
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Also copy to clipboard as fallback
  navigator.clipboard.writeText(url).then(() => {
    toast.success('Link copiado! Se não abriu, cole numa nova aba do navegador.', { duration: 5000 });
  }).catch(() => {});
}

function calcAge(dobStr) {
  if (!dobStr) return null;
  const [y, m, d] = dobStr.split('-').map(Number);
  const today = new Date();
  let age = today.getFullYear() - y;
  if (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) age--;
  return age;
}

function getFilaNome(pessoa, filas) {
  const filaId = pessoa.fila_id;
  if (filaId) {
    const fila = (filas || []).find(f => f.fila_id === filaId);
    if (fila) return fila.nome;
  }
  const age = calcAge(pessoa.data_nascimento);
  if (age === null) return null;
  for (const f of (filas || [])) {
    const ini = f.idade_inicial;
    const fim = f.idade_final;
    if (fim !== null && fim !== undefined) {
      if (age >= ini && age <= fim) return f.nome;
    } else {
      if (age >= ini) return f.nome;
    }
  }
  return null;
}

export default function AniversariantesTab({ pessoas }) {
  const [mesSelecionado, setMesSelecionado] = useState(new Date().getMonth());

  const contagemPorMes = Array.from({ length: 12 }, (_, i) =>
    (pessoas || []).filter(p => p.data_nascimento && parseInt(p.data_nascimento.split('-')[1], 10) - 1 === i).length
  );

  const aniversariantes = (pessoas || [])
    .filter(p => {
      if (!p.data_nascimento) return false;
      const mes = parseInt(p.data_nascimento.split('-')[1], 10) - 1;
      return mes === mesSelecionado;
    })
    .map(p => {
      const parts = p.data_nascimento.split('-');
      return { ...p, dia: parseInt(parts[2], 10), idade: calcAge(p.data_nascimento) };
    })
    .sort((a, b) => a.dia - b.dia);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <div className="lg:col-span-1">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center flex-shrink-0">
                <Cake className="w-5 h-5 text-pink-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Meses</h2>
                </div>
                <p className="text-sm text-slate-500">Selecione o mes</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-1" data-testid="meses-menu">
              {MESES.map((mes, i) => (
                <button key={i} onClick={() => setMesSelecionado(i)} data-testid={`mes-${i}`}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-200 flex items-center justify-between text-sm ${mesSelecionado === i ? 'bg-pink-50 text-pink-700 font-semibold border-2 border-pink-300' : 'text-slate-700 hover:bg-slate-50 border-2 border-transparent'}`}>
                  {mes}
                  <Badge className={`text-xs font-semibold ${mesSelecionado === i ? 'bg-pink-100 text-pink-700' : 'bg-slate-100 text-slate-500'}`}>{contagemPorMes[i]}</Badge>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-3">
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-pink-100 flex items-center justify-center flex-shrink-0">
                <Cake className="w-5 h-5 text-pink-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Aniversariantes de {MESES[mesSelecionado]}</h2>
                <p className="text-sm text-slate-500">Ordenados por dia do mes</p>
              </div>
              <Badge className="bg-pink-100 text-pink-700 text-xs font-semibold ml-1">{aniversariantes.length}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="aniversariantes-list">
              {aniversariantes.map((p) => (
                <div key={p.pessoa_id} className="px-4 py-3 border-b border-slate-100 last:border-b-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                      {p.dia}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm text-slate-900 truncate">{p.nome_completo}</p>
                        {p.idade !== null && <Badge variant="outline" className="text-xs">{p.idade} anos</Badge>}
                        {p.genero && <Badge variant="outline" className="text-xs">{p.genero}</Badge>}
                        <span className="flex items-center gap-1 text-sm text-slate-500">
                          <Calendar className="w-3.5 h-3.5" />{String(p.dia).padStart(2, '0')}/{String(mesSelecionado + 1).padStart(2, '0')}
                        </span>
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-3">
                      {p.whatsapp && (
                        <button onClick={() => enviarWhatsApp(p.nome_completo, p.whatsapp)} data-testid={`whatsapp-${p.pessoa_id}`}
                          className="w-8 h-8 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center transition-colors cursor-pointer" title="Copiar link do WhatsApp">
                          <MessageCircle className="w-4 h-4 text-white" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {aniversariantes.length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <Cake className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nenhum aniversariante em {MESES[mesSelecionado]}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
